import axios from "axios";

// ─────────────────────────────────────────────────────────────
// API URL CONFIGURATION
// REACT_APP_API_URL can override this per deployment environment.
// Falls back to the main backend for local `npm start`.
// ─────────────────────────────────────────────────────────────
const API_BASE_URL =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
  "https://api.estateone-access.com/api";

// Create axios instance for admin API
export const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminProfile");
      localStorage.removeItem("adminUser");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// Bulk Visit Management API
export const bulkVisitApi = {
  getAll: async (params) => {
    const response = await adminApi.get("/admin/bulk-visits", { params });
    return response.data.data || response.data;
  },
  getByUser: async (userId) => {
    const response = await adminApi.get(`/admin/bulk-visits?userId=${userId}`);
    return response.data.data || response.data;
  },
  approve: async (id) => {
    const response = await adminApi.post(`/admin/bulk-visits/${id}/approve`);
    return response.data;
  },
  reject: async (id, reason) => {
    const response = await adminApi.post(`/admin/bulk-visits/${id}/reject`, {
      reason,
    });
    return response.data;
  },
};

// Broadcast Management API
export const broadcastApi = {
  getAll: async (params) => {
    console.log("broadcastApi.getAll called with params:", params);
    const response = await adminApi.get("/admin/broadcasts", { params });
    console.log("API Response:", response.data);
    return response.data.data || response.data;
  },
  getByEstate: async (params) => {
    const response = await adminApi.get("/broadcasts", { params });
    return response.data.data || response.data;
  },
  create: async (data) => {
    const response = await adminApi.post("/admin/broadcasts", data);
    return response.data;
  },
};

// Estate Management API
export const estateApi = {
  getAll: async () => {
    const response = await adminApi.get("/admin/estates");
    return response.data.data || response.data;
  },
  getById: async (id) => {
    const response = await adminApi.get(`/admin/estates/${id}`);
    return response.data.data || response.data;
  },
  create: (data) => adminApi.post("/admin/estates", data),
  update: (id, data) => adminApi.put(`/admin/estates/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/estates/${id}`),
  getStats: (id) => adminApi.get(`/admin/estates/${id}/stats`),
};

export const residentRegistrationApi = {
  getAll: async (params) => {
    const response = await adminApi.get("/admin/residents/registrations", {
      params,
    });
    return response.data.data || response.data;
  },
  create: (data) => adminApi.post("/admin/residents/registrations", data),
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return adminApi.post("/admin/residents/documents/upload", formData);
  },
  getById: async (id) => {
    const response = await adminApi.get(`/admin/residents/registrations/${id}`);
    return response.data.data || response.data;
  },
  approve: (id, data) =>
    adminApi.post(`/admin/residents/registrations/${id}/approve`, data),
  reject: (id, data) =>
    adminApi.post(`/admin/residents/registrations/${id}/reject`, data),
};

export const propertyUnitApi = {
  getAll: async (params) => {
    const response = await adminApi.get("/admin/residents/property-units", {
      params,
    });
    return response.data.data || response.data;
  },
  create: (data) => adminApi.post("/admin/residents/property-units", data),
  update: (id, data) =>
    adminApi.put(`/admin/residents/property-units/${id}`, data),
};

// User Management API
export const userApi = {
  getAll: async (params) => {
    const response = await adminApi.get("/admin/users", { params });
    return response.data.data || response.data;
  },
  getById: async (id) => {
    const response = await adminApi.get(`/admin/users/${id}`);
    return response.data.data || response.data;
  },
  create: (data) => adminApi.post("/admin/users", data),
  updateBulk: (id, data) =>
    adminApi.post(`/admin/users/${id}/bulk-visit-permission`, data),
  update: (id, data) => adminApi.put(`/admin/users/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/users/${id}`),
  getByEstate: async (estateId) => {
    const response = await adminApi.get(`/admin/estates/${estateId}/users`);
    return response.data.data || response.data;
  },
  assignToEstate: (userId, estateId) =>
    adminApi.post(`/admin/users/${userId}/assign-estate`, { estateId }),
  removeFromEstate: (userId, estateId) =>
    adminApi.delete(`/admin/users/${userId}/estates/${estateId}`),
};

// Security Personnel API
export const securityApi = {
  getAll: async (params) => {
    const response = await adminApi.get("/admin/security", { params });
    return response.data.data || response.data;
  },
  getById: async (id) => {
    const response = await adminApi.get(`/admin/security/${id}`);
    return response.data.data || response.data;
  },
  create: (data) => adminApi.post("/admin/security", data),
  update: (id, data) => adminApi.put(`/admin/security/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/security/${id}`),
  getByEstate: async (estateId) => {
    const response = await adminApi.get(`/admin/estates/${estateId}/security`);
    return response.data.data || response.data;
  },
  assignToEstate: (securityId, estateId) =>
    adminApi.post(`/admin/security/${securityId}/assign-estate`, { estateId }),
  removeFromEstate: (securityId, estateId) =>
    adminApi.delete(`/admin/security/${securityId}/estates/${estateId}`),
};

// Dashboard API
export const dashboardApi = {
  getStats: async () => {
    const response = await adminApi.get("/admin/dashboard/stats");
    return response.data.data || response.data;
  },
  getRecentActivities: async () => {
    const response = await adminApi.get("/admin/dashboard/activities");
    return response.data.data || response.data;
  },
  getOverview: async () => {
    const response = await adminApi.get("/admin/dashboard/overview");
    return response.data.data || response.data;
  },
};

// Visitor Management API
export const visitorApi = {
  getAll: (params) => adminApi.get("/admin/visitors", { params }),
  getByEstate: (estateId) =>
    adminApi.get(`/admin/estates/${estateId}/visitors`),
  getById: (id) => adminApi.get(`/admin/visitors/${id}`),
  update: (id, data) => adminApi.put(`/admin/visitors/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/visitors/${id}`),
  getLogs: (params) => adminApi.get("/admin/visitors/logs", { params }),
};

// Access Logs API
export const accessLogApi = {
  getAll: (params) => adminApi.get("/admin/logs", { params }),
  getByEstate: (estateId) => adminApi.get(`/admin/estates/${estateId}/logs`),
  getByUser: (userId) => adminApi.get(`/admin/users/${userId}/logs`),
  getBySecurity: (securityId) =>
    adminApi.get(`/admin/security/${securityId}/logs`),
  export: (params) =>
    adminApi.get("/admin/logs/export", { params, responseType: "blob" }),
    getCvTransactions: (params) => adminApi.get("/admin/logs/cv", { params }),
    syncCvTransactions: () => adminApi.post("/admin/logs/cv/sync"),
    requestHistorical: (data) => adminApi.post("/admin/logs/cv/historical", data),
    exportCvTransactions: (params) =>
      adminApi.get("/admin/logs/cv/export", { params, responseType: "blob" }),
};

// QR Code Management API
export const qrCodeApi = {
  generateForEstate: (estateId) =>
    adminApi.post(`/admin/estates/${estateId}/qr-code`),
  getHistory: (estateId) =>
    adminApi.get(`/admin/estates/${estateId}/qr-history`),
  revoke: (qrCodeId) => adminApi.delete(`/admin/qr-codes/${qrCodeId}`),
  verify: (qrCode) => adminApi.post("/admin/qr-codes/verify", { qrCode }),
};

// Emergency Management API
export const emergencyApi = {
  getAll: (params) => adminApi.get("/admin/emergency", { params }),
  getByEstate: (estateId) =>
    adminApi.get(`/admin/estates/${estateId}/emergency`),
  getById: (id) => adminApi.get(`/admin/emergency/${id}`),
  update: (id, data) => adminApi.put(`/admin/emergency/${id}`, data),
  resolve: (id) => adminApi.post(`/admin/emergency/${id}/resolve`),
  delete: (id) => adminApi.delete(`/admin/emergency/${id}`),
};

// Maintenance Management API
export const maintenanceApi = {
  getAll: (params) => adminApi.get("/admin/maintenance", { params }),
  getByEstate: (estateId) =>
    adminApi.get(`/admin/estates/${estateId}/maintenance`),
  getById: (id) => adminApi.get(`/admin/maintenance/${id}`),
  update: (id, data) => adminApi.put(`/admin/maintenance/${id}`, data),
  assign: (id, assigneeId) =>
    adminApi.post(`/admin/maintenance/${id}/assign`, { assigneeId }),
  complete: (id) => adminApi.post(`/admin/maintenance/${id}/complete`),
  delete: (id) => adminApi.delete(`/admin/maintenance/${id}`),
};

// Notification Management API
export const notificationApi = {
  getAll: (params) => adminApi.get("/admin/notifications", { params }),
  getByEstate: (estateId) =>
    adminApi.get(`/admin/estates/${estateId}/notifications`),
  send: (data) => adminApi.post("/admin/notifications", data),
  update: (id, data) => adminApi.put(`/admin/notifications/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/notifications/${id}`),
  markAsRead: (id) => adminApi.put(`/admin/notifications/${id}/read`),
  markAllAsRead: () => adminApi.put("/admin/notifications/mark-all-read"),
};

// System Settings API
export const settingsApi = {
  get: () => adminApi.get("/admin/settings"),
  update: (data) => adminApi.put("/admin/settings", data),
  getBackup: () => adminApi.get("/admin/settings/backup"),
  restore: (data) => adminApi.post("/admin/settings/restore", data),
};

// Admin Management API
export const adminManagementApi = {
  getAll: async (params) => {
    const response = await adminApi.get("/auth/admin/all", { params });
    return response.data.data || response.data;
  },
  getById: async (id) => {
    const response = await adminApi.get(`/auth/admin/${id}`);
    return response.data.data || response.data;
  },
  create: async (data) => {
    const response = await adminApi.post("/auth/admin/create", data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await adminApi.put(`/auth/admin/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await adminApi.delete(`/auth/admin/${id}`);
    return response.data;
  },
  assignToEstate: async (adminId, estateId) => {
    const response = await adminApi.post(
      `/auth/admin/${adminId}/assign-estate`,
      { estateId },
    );
    return response.data;
  },
  removeFromEstate: async (adminId, estateId) => {
    const response = await adminApi.delete(
      `/auth/admin/${adminId}/estates/${estateId}`,
    );
    return response.data;
  },
};

export const adminAccountApi = {
  getMe: async () => {
    const response = await adminApi.get("/auth/admin/me");
    return response.data.data || response.data;
  },
  updateProfile: async (data) => {
    const response = await adminApi.put("/auth/admin/profile", data);
    return response.data.data || response.data;
  },
  changePassword: async (data) => {
    const response = await adminApi.put("/auth/admin/change-password", data);
    return response.data;
  },
};

// Estate Roles API
export const estateRolesApi = {
  getAll: async (estateId) => {
    const response = await adminApi.get(`/admin/estates/${estateId}/roles`);
    return response.data.data || response.data;
  },
  getById: async (estateId, roleId) => {
    const response = await adminApi.get(
      `/admin/estates/${estateId}/roles/${roleId}`,
    );
    return response.data.data || response.data;
  },
  create: async (estateId, data) => {
    const response = await adminApi.post(
      `/admin/estates/${estateId}/roles`,
      data,
    );
    return response.data;
  },
  update: async (estateId, roleId, data) => {
    const response = await adminApi.put(
      `/admin/estates/${estateId}/roles/${roleId}`,
      data,
    );
    return response.data;
  },
  delete: async (estateId, roleId) => {
    const response = await adminApi.delete(
      `/admin/estates/${estateId}/roles/${roleId}`,
    );
    return response.data;
  },
};

// Estate Super Admin Assignment API
export const estateSuperAdminApi = {
  assign: async (estateId, adminId) => {
    const response = await adminApi.post(
      `/admin/estates/${estateId}/super-admins`,
      { adminId },
    );
    return response.data;
  },
  remove: async (estateId, adminId) => {
    const response = await adminApi.delete(
      `/admin/estates/${estateId}/super-admins/${adminId}`,
    );
    return response.data;
  },
  getAll: async (estateId) => {
    const response = await adminApi.get(
      `/admin/estates/${estateId}/super-admins`,
    );
    return response.data.data || response.data;
  },
};

// Export all APIs
export default {
  estate: estateApi,
  user: userApi,
  security: securityApi,
  dashboard: dashboardApi,
  visitor: visitorApi,
  accessLog: accessLogApi,
  qrCode: qrCodeApi,
  emergency: emergencyApi,
  maintenance: maintenanceApi,
  notification: notificationApi,
  settings: settingsApi,
  bulkVisit: bulkVisitApi,
  broadcast: broadcastApi,
  adminManagement: adminManagementApi,
  adminAccount: adminAccountApi,
  estateRoles: estateRolesApi,
  residentRegistration: residentRegistrationApi,
  propertyUnit: propertyUnitApi,
};

